/**
 * ============================================================
 * System       : CSAF Alert Finance
 * File         : frontend/src/pages/Manual.jsx
 * Description  : System user manual
 * ============================================================
 */

import React, { useState, useEffect } from 'react';

const SECTIONS = [
  { id: 'overview',       label: 'ภาพรวมระบบ' },
  { id: 'login',          label: 'การเข้าสู่ระบบ' },
  { id: 'dashboard',      label: 'Dashboard' },
  { id: 'records',        label: 'รายการสำรองจ่าย' },
  { id: 'report',         label: 'รายงาน' },
  { id: 'history',        label: 'ประวัติการดำเนินการ' },
  { id: 'notifications',  label: 'การแจ้งเตือน' },
  { id: 'companies',      label: 'บริษัท' },
  { id: 'users',          label: 'ผู้ใช้งาน' },
  { id: 'settings',       label: 'ตั้งค่าระบบ' },
  { id: 'roles',          label: 'สิทธิ์ผู้ใช้งาน' },
  { id: 'line',           label: 'LINE & Email Notification' },
];

function Section({ id, title, icon, children }) {
  return (
    <section id={id} style={{ marginBottom: 40, scrollMarginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: '2px solid #E5E7EB' }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>{title}</h2>
      </div>
      <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.75 }}>
        {children}
      </div>
    </section>
  );
}

function Tag({ color = '#1D9E75', bg = '#F0FDF4', children }) {
  return (
    <span style={{ display: 'inline-block', background: bg, color, fontWeight: 700, fontSize: '0.75rem', padding: '2px 10px', borderRadius: 12, margin: '0 2px' }}>
      {children}
    </span>
  );
}

function Note({ type = 'info', children }) {
  const styles = {
    info:    { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF', icon: 'ℹ️' },
    warn:    { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E', icon: '⚠️' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D', icon: '✅' },
    admin:   { bg: '#FAF5FF', border: '#DDD6FE', color: '#6D28D9', icon: '🔐' },
  };
  const s = styles[type];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '10px 14px', margin: '12px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
      <div style={{ color: s.color, fontSize: '0.85rem', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#F9FAFB' }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', border: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '8px 14px', border: '1px solid #E5E7EB', color: '#374151', verticalAlign: 'top' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Step({ num, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, margin: '8px 0', alignItems: 'flex-start' }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#1D9E75', color: '#fff', fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{num}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export default function Manual() {
  const [active, setActive] = useState('overview');

  useEffect(() => {
    const handler = () => {
      const offsets = SECTIONS.map(s => {
        const el = document.getElementById(s.id);
        return el ? { id: s.id, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);
      const visible = offsets.filter(o => o.top <= 80);
      if (visible.length) setActive(visible[visible.length - 1].id);
    };
    const scrollEl = document.querySelector('main');
    scrollEl?.addEventListener('scroll', handler);
    return () => scrollEl?.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    const scrollEl = document.querySelector('main');
    if (el && scrollEl) {
      const top = el.offsetTop - 24;
      scrollEl.scrollTo({ top, behavior: 'smooth' });
    }
    setActive(id);
  };

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

      {/* TOC — sticky left */}
      <aside className="no-print" style={{ width: 185, flexShrink: 0, position: 'sticky', top: 0 }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '14px 0', overflow: 'hidden' }}>
          <div style={{ padding: '0 14px 10px', borderBottom: '1px solid #F3F4F6', marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151' }}>สารบัญ</div>
          </div>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 14px', fontSize: '0.8rem', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all .12s',
                background: active === s.id ? '#F0FDF4' : 'transparent',
                color: active === s.id ? '#1D9E75' : '#6B7280',
                fontWeight: active === s.id ? 700 : 400,
                borderLeft: active === s.id ? '3px solid #1D9E75' : '3px solid transparent',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => window.print()}
          style={{
            marginTop: 12, width: '100%', padding: '8px 0', borderRadius: 8,
            background: '#1D9E75', color: '#fff', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: '0.83rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          🖨️ พิมพ์ / PDF
        </button>
      </aside>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Title */}
        <div style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #059669 100%)', borderRadius: 14, padding: '28px 32px', marginBottom: 28, color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 900 }}>C</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.2 }}>CSAF Alert Finance</div>
              <div style={{ fontSize: '0.88rem', opacity: 0.85 }}>Charoensin Alert Finance — คู่มือการใช้งานระบบ</div>
            </div>
          </div>
          <div style={{ fontSize: '0.82rem', opacity: 0.75 }}>เวอร์ชัน 1.0.0 · พฤษภาคม 2569 · Chokchai Thainkhao (Head of IT)</div>
        </div>

        {/* ── 1 ภาพรวมระบบ ── */}
        <Section id="overview" title="ภาพรวมระบบ" icon="🏦">
          <p>
            <strong>CSAF (Charoensin Alert Finance)</strong> คือระบบติดตามและแจ้งเตือนการเบิกคืนเงินสำรองจ่ายอัตโนมัติ
            ออกแบบมาเพื่อช่วยฝ่ายการเงินของกลุ่มบริษัทเจริญสินในการติดตามพนักงานที่ยังไม่ส่งเอกสารเบิกคืน
            ภายในระยะเวลาที่กำหนด
          </p>
          <Table
            headers={['ฟีเจอร์', 'คำอธิบาย']}
            rows={[
              ['Dashboard', 'สรุปภาพรวมรายการสำรองจ่ายทั้งหมด'],
              ['รายการสำรองจ่าย', 'เพิ่ม / นำเข้า / เคลียร์รายการ'],
              ['แจ้งเตือนอัตโนมัติ', 'ส่ง Email + LINE ที่ครบกำหนด 5 / 10 / 15 วัน'],
              ['รายงาน', 'Export PDF สรุปรายการ'],
              ['จัดการบริษัท', 'เพิ่ม / แก้ไขบริษัท และกำหนดสิทธิ์ผู้ใช้'],
              ['จัดการผู้ใช้', 'เพิ่ม / แก้ไขบัญชีผู้ใช้งาน'],
            ]}
          />
          <Note type="info">
            ระบบทำงานบน Server ตลอด 24 ชั่วโมง โดย Scheduler จะส่งแจ้งเตือนอัตโนมัติทุกวันเวลา <strong>08:00 น.</strong> (เวลาประเทศไทย)
          </Note>
        </Section>

        {/* ── 2 การเข้าสู่ระบบ ── */}
        <Section id="login" title="การเข้าสู่ระบบ" icon="🔑">
          <Step num="1">เปิด Browser แล้วไปที่ URL ของระบบ</Step>
          <Step num="2">กรอก <strong>Email</strong> และ <strong>รหัสผ่าน</strong> ที่ผู้ดูแลระบบกำหนดให้</Step>
          <Step num="3">กด <Tag>เข้าสู่ระบบ</Tag> — ระบบจะพาไปหน้า Dashboard โดยอัตโนมัติ</Step>
          <Note type="warn">
            หากลืมรหัสผ่าน ติดต่อผู้ดูแลระบบ (Admin) เพื่อรีเซ็ตรหัสผ่านให้
          </Note>
          <p style={{ marginTop: 12 }}>Token การเข้าสู่ระบบจะหมดอายุหลังจากไม่ได้ใช้งานเป็นเวลานาน ระบบจะออกจากระบบอัตโนมัติและให้กลับมา Login ใหม่</p>
        </Section>

        {/* ── 3 Dashboard ── */}
        <Section id="dashboard" title="Dashboard" icon="📊">
          <p>หน้าแรกของระบบ แสดงสรุปภาพรวมรายการสำรองจ่ายทั้งหมดแบบ Real-time</p>
          <Table
            headers={['การ์ด', 'ความหมาย']}
            rows={[
              ['รายการทั้งหมด', 'จำนวน Record ทั้งหมดในระบบ (รวมที่เคลียร์แล้ว)'],
              ['รอดำเนินการ', 'รายการที่ยังไม่เคลียร์'],
              ['เคลียร์แล้ว', 'รายการที่ส่งเอกสารเรียบร้อยแล้ว'],
              ['ยอดคงค้างรวม', 'ยอดเงินรวมของรายการที่ยังไม่เคลียร์'],
            ]}
          />
          <p>กราฟด้านล่างแสดงการกระจายของยอดเงินสำรองจ่ายแต่ละรายการ และตารางรายการที่<strong>ใกล้ครบกำหนด</strong>เรียงตามวันที่สำรองจ่ายเก่าสุด</p>
        </Section>

        {/* ── 4 รายการสำรองจ่าย ── */}
        <Section id="records" title="รายการสำรองจ่าย" icon="📋">
          <p>หน้าหลักสำหรับจัดการรายการสำรองจ่ายทั้งหมด</p>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '18px 0 8px' }}>เพิ่มรายการใหม่</h3>
          <Step num="1">กดปุ่ม <Tag>+ เพิ่มรายการ</Tag> มุมขวาบน</Step>
          <Step num="2">กรอกข้อมูล: ชื่อ, บริษัท, อีเมล, ยอดเงินสำรองจ่าย, วันที่สำรองจ่าย</Step>
          <Step num="3">กด <Tag>บันทึก</Tag></Step>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '18px 0 8px' }}>นำเข้าไฟล์ TXT (Batch)</h3>
          <p>รองรับไฟล์ .TXT รูปแบบ <strong>Fixed-width</strong> ที่ Export มาจากระบบ CST (ระบบเงินเดือน) โดยระบบจะอ่านและแปลงข้อมูลอัตโนมัติ</p>
          <Step num="1">กดปุ่ม <Tag>📥 นำเข้าไฟล์</Tag></Step>
          <Step num="2">เลือกไฟล์ .TXT</Step>
          <Step num="3">ตรวจสอบ Preview และกด <Tag>นำเข้า</Tag></Step>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '18px 0 8px' }}>เคลียร์รายการ</h3>
          <p>เมื่อพนักงานส่งเอกสารเบิกคืนแล้ว ให้กดปุ่ม <Tag bg="#F0FDF4" color="#15803D">✅ เคลียร์</Tag> ที่รายการนั้น ระบบจะหยุดส่งแจ้งเตือนสำหรับรายการดังกล่าว</p>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '18px 0 8px' }}>ส่งแจ้งเตือนด้วยตนเอง</h3>
          <p>นอกจากระบบอัตโนมัติ สามารถกดส่งแจ้งเตือนทีละรายการได้โดยกดปุ่ม <Tag bg="#EFF6FF" color="#1D4ED8">📤 ส่ง Alert</Tag></p>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '18px 0 8px' }}>Filter และค้นหา</h3>
          <Table
            headers={['Filter', 'ความหมาย']}
            rows={[
              ['ทั้งหมด', 'แสดงทุกรายการ (รวมเคลียร์แล้ว)'],
              ['รอดำเนินการ', 'เฉพาะรายการที่ยังไม่เคลียร์'],
              ['ครบกำหนด', 'รายการที่เลยกำหนด 5 วันขึ้นไปและยังไม่เคลียร์'],
              ['เคลียร์แล้ว', 'รายการที่ปิดแล้ว'],
            ]}
          />
        </Section>

        {/* ── 5 รายงาน ── */}
        <Section id="report" title="รายงาน" icon="📄">
          <p>สร้างและ Export รายงานสรุปรายการสำรองจ่ายเป็นไฟล์ PDF</p>
          <Step num="1">เลือกช่วงวันที่ (ถ้าต้องการ)</Step>
          <Step num="2">เลือก Filter: รายการรอดำเนินการ / ทั้งหมด</Step>
          <Step num="3">กด <Tag>📥 Export PDF</Tag> — ไฟล์จะดาวน์โหลดอัตโนมัติ</Step>
          <Note type="info">
            PDF ถูก Generate บน Server โดยใช้ Puppeteer/Chromium ทำให้รูปแบบตรงกับที่แสดงในหน้าจอ
          </Note>
        </Section>

        {/* ── 6 ประวัติ ── */}
        <Section id="history" title="ประวัติการดำเนินการ" icon="🕐">
          <p>บันทึกทุก Action ที่เกิดขึ้นในระบบ เรียงจากล่าสุด</p>
          <Table
            headers={['ประเภท', 'หมายความว่า']}
            rows={[
              ['🤖 อัตโนมัติ', 'Scheduler ส่งแจ้งเตือนอัตโนมัติ 08:00 น.'],
              ['📤 ส่ง Alert', 'ผู้ใช้กดส่งแจ้งเตือนด้วยตนเอง'],
              ['✅ เคลียร์', 'ผู้ใช้เคลียร์รายการ'],
              ['📥 นำเข้า', 'ผู้ใช้นำเข้าไฟล์ TXT'],
            ]}
          />
          <p>สามารถ <strong>ค้นหา</strong> ตามชื่อ, รายละเอียด, หรือเลขรายการ และ <strong>Filter</strong> ตามประเภทได้</p>
        </Section>

        {/* ── 7 การแจ้งเตือน ── */}
        <Section id="notifications" title="การแจ้งเตือน" icon="🔔">
          <Note type="admin">เมนูนี้แสดงเฉพาะผู้ดูแลระบบ (Admin) เท่านั้น</Note>
          <p>หน้านี้ใช้ตั้งค่า Auto Mode และดูรายการที่รอการแจ้งเตือน</p>
          <Table
            headers={['รอบแจ้งเตือน', 'วันที่นับจากวันสำรองจ่าย', 'สี']}
            rows={[
              ['ครั้งที่ 1', '5 วัน (ค่าเริ่มต้น)', '🟢 เขียว'],
              ['ครั้งที่ 2', '10 วัน (ค่าเริ่มต้น)', '🟡 เหลือง'],
              ['ครั้งที่ 3', '15 วัน (ค่าเริ่มต้น)', '🔴 แดง'],
            ]}
          />
          <Note type="info">
            จำนวนวันสำหรับแต่ละรอบสามารถปรับได้ในหน้า <strong>ตั้งค่าระบบ</strong>
          </Note>
          <p>เปิด <strong>Auto Mode</strong> เพื่อให้ระบบส่งแจ้งเตือนอัตโนมัติทุกวัน 08:00 น. โดยไม่ต้องกดด้วยตนเอง</p>
        </Section>

        {/* ── 8 บริษัท ── */}
        <Section id="companies" title="บริษัท" icon="🏢">
          <p>จัดการรายชื่อบริษัทและกำหนดว่าผู้ใช้งานคนใดสังกัดบริษัทใด</p>
          <Note type="admin">การเพิ่ม / แก้ไข / ลบบริษัท และการเพิ่ม/ลบผู้ใช้งานออกจากบริษัท — เฉพาะ Admin</Note>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>เพิ่มบริษัทใหม่</h3>
          <Step num="1">กดปุ่ม <Tag>+ เพิ่มบริษัท</Tag></Step>
          <Step num="2">กรอกชื่อบริษัท แล้วกด <Tag>บันทึก</Tag></Step>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>กำหนดผู้ใช้งานให้บริษัท</h3>
          <p>ในการ์ดของแต่ละบริษัท กด <Tag bg="#EFF6FF" color="#1D4ED8">+ เพิ่มผู้ใช้</Tag> แล้วเลือกผู้ใช้ที่ต้องการ ผู้ใช้นั้นจะเห็นเฉพาะรายการของบริษัทที่ตนสังกัด</p>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>เปลี่ยนชื่อบริษัท</h3>
          <p>เมื่อเปลี่ยนชื่อบริษัท ระบบจะอัปเดตชื่อในรายการสำรองจ่ายและบัญชีผู้ใช้ทั้งหมดโดยอัตโนมัติ</p>
        </Section>

        {/* ── 9 ผู้ใช้งาน ── */}
        <Section id="users" title="ผู้ใช้งาน" icon="👥">
          <p>จัดการบัญชีผู้ใช้งานในระบบ</p>
          <Note type="admin">การเพิ่ม / ลบ / แก้ไข Role ผู้ใช้ — เฉพาะ Admin เท่านั้น</Note>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>เพิ่มผู้ใช้งานใหม่</h3>
          <Step num="1">กดปุ่ม <Tag>+ เพิ่มผู้ใช้</Tag></Step>
          <Step num="2">กรอก ชื่อ, นามสกุล, Email, รหัสผ่าน, บทบาท (Admin / User)</Step>
          <Step num="3">กด <Tag>บันทึก</Tag></Step>
          <Note type="warn">Email จะใช้เป็น Username สำหรับ Login และเป็นที่อยู่รับ Alert — ต้องไม่ซ้ำกัน</Note>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>ข้อจำกัด</h3>
          <Table
            headers={['การกระทำ', 'ข้อจำกัด']}
            rows={[
              ['ลบบัญชีตัวเอง', 'ไม่สามารถทำได้'],
              ['เปลี่ยน Role ตัวเอง', 'ไม่สามารถทำได้ (ต้องให้ Admin อื่นเปลี่ยน)'],
              ['ดูผู้ใช้อื่น (User role)', 'เห็นเฉพาะข้อมูลของตัวเอง'],
            ]}
          />
        </Section>

        {/* ── 10 ตั้งค่าระบบ ── */}
        <Section id="settings" title="ตั้งค่าระบบ" icon="⚙️">
          <Note type="admin">เฉพาะผู้ดูแลระบบ (Admin) เท่านั้น</Note>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>การแจ้งเตือน (Alert Threshold)</h3>
          <Table
            headers={['ฟิลด์', 'คำอธิบาย']}
            rows={[
              ['Alert 1 (วัน)', 'จำนวนวันก่อนส่งแจ้งเตือนครั้งที่ 1 (ค่าเริ่มต้น: 5 วัน)'],
              ['Alert 2 (วัน)', 'จำนวนวันก่อนส่งแจ้งเตือนครั้งที่ 2 (ค่าเริ่มต้น: 10 วัน)'],
              ['Alert 3 (วัน)', 'จำนวนวันก่อนส่งแจ้งเตือนครั้งที่ 3 (ค่าเริ่มต้น: 15 วัน)'],
              ['Admin Email', 'อีเมลสำหรับรับ CC ทุกการแจ้งเตือน'],
            ]}
          />

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>LINE Notify</h3>
          <Table
            headers={['ฟิลด์', 'คำอธิบาย']}
            rows={[
              ['Channel Access Token', 'Token จาก LINE Developers Console'],
              ['Group ID', 'ID ของ LINE Group ที่ต้องการส่ง'],
              ['สถานะ LINE Notify', 'เปิด/ปิดการส่ง LINE'],
            ]}
          />

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>SMTP Email</h3>
          <Table
            headers={['ฟิลด์', 'ตัวอย่าง (Gmail)']}
            rows={[
              ['SMTP Host', 'smtp.gmail.com'],
              ['Port', '587'],
              ['Secure', 'false (STARTTLS)'],
              ['Email ผู้ส่ง', 'yourname@gmail.com'],
              ['App Password', 'xxxx xxxx xxxx xxxx (Google App Password)'],
            ]}
          />
          <Note type="info">
            Gmail ต้องเปิด <strong>2-Step Verification</strong> และสร้าง <strong>App Password</strong> ที่ myaccount.google.com/apppasswords
          </Note>
        </Section>

        {/* ── 11 สิทธิ์ ── */}
        <Section id="roles" title="สิทธิ์ผู้ใช้งาน" icon="🔐">
          <Table
            headers={['ฟีเจอร์ / เมนู', 'Admin', 'User']}
            rows={[
              ['Dashboard', '✅', '✅ (เฉพาะบริษัทตน)'],
              ['รายการสำรองจ่าย', '✅ ทุกบริษัท', '✅ เฉพาะบริษัทตน'],
              ['รายงาน', '✅', '✅'],
              ['ประวัติการดำเนินการ', '✅', '✅'],
              ['การแจ้งเตือน', '✅', '❌'],
              ['บริษัท — ดูรายการ', '✅ ทุกบริษัท', '✅ เฉพาะบริษัทตน'],
              ['บริษัท — เพิ่ม / แก้ไข / ลบ', '✅', '❌'],
              ['ผู้ใช้งาน — ดูรายการ', '✅ ทุกคน', '✅ เฉพาะตัวเอง'],
              ['ผู้ใช้งาน — เพิ่ม / แก้ไข / ลบ', '✅', '❌'],
              ['ตั้งค่าระบบ', '✅', '❌'],
            ]}
          />
        </Section>

        {/* ── 12 LINE & Email ── */}
        <Section id="line" title="LINE & Email Notification" icon="📬">
          <p>ระบบรองรับการแจ้งเตือน 2 ช่องทาง ส่งพร้อมกันทุกครั้งที่ Scheduler ทำงาน</p>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>Email</h3>
          <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
            <li>ส่งถึงอีเมลของพนักงานแต่ละคน (ทีละคน)</li>
            <li>Admin Email จะได้รับ CC ทุกฉบับ</li>
            <li>Subject บอกรอบที่ส่ง (ครั้งที่ 1 / 2 / 3)</li>
          </ul>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>LINE (Flex Message)</h3>
          <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
            <li>ส่ง <strong>1 การ์ดต่อรอบ</strong> ไปยัง LINE Group</li>
            <li>รวมทุกบริษัทและทุกคนที่ครบกำหนดรอบนั้นไว้ในการ์ดเดียว</li>
            <li>สีการ์ดบอกความเร่งด่วน: เขียว (รอบ 1) → เหลือง (รอบ 2) → แดง (รอบ 3)</li>
            <li>แสดงยอดรวมทั้งหมดท้ายการ์ด</li>
          </ul>

          <Note type="success">
            หากวันนั้นมีรายการครบกำหนดหลายรอบพร้อมกัน ระบบจะส่ง LINE <strong>แยกการ์ดตามรอบ</strong> เช่น รอบ 1 การ์ดหนึ่ง + รอบ 2 อีกการ์ดหนึ่ง
          </Note>

          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', margin: '16px 0 8px' }}>ทดสอบการส่ง</h3>
          <p>ในหน้า <strong>ตั้งค่าระบบ</strong> มีปุ่มทดสอบ 2 แบบ:</p>
          <Table
            headers={['ปุ่ม', 'ส่งอะไร']}
            rows={[
              ['💬 ทดสอบส่ง LINE', 'การ์ดยืนยันการเชื่อมต่อ (ใบเดียว)'],
              ['📊 ทดสอบส่ง LINE แบบสรุปรายบริษัท', 'จำลองการ์ดสรุป 3 รอบ (2 บริษัท × 3 คน)'],
              ['✉️ ทดสอบส่ง Email', 'ส่ง Email ทดสอบไปยัง Admin Email'],
            ]}
          />
        </Section>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 20, marginTop: 8, textAlign: 'center', color: '#9CA3AF', fontSize: '0.78rem' }}>
          CSAF Alert Finance · เวอร์ชัน 1.0.0 · © 2026 Chokchai Thainkhao · AI Assisted (Claude)
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, html { background: white; }
          @page { margin: 20mm; }
        }
      `}</style>
    </div>
  );
}
